import { memo, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
  HStack,
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
} from '@chakra-ui/react'

interface CreateProjectModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
}

const COLORS = [
  { value: 'blue', label: 'Bleu', bg: 'blue.500' },
  { value: 'indigo', label: 'Indigo', bg: 'indigo.500' },
  { value: 'purple', label: 'Violet', bg: 'purple.500' },
  { value: 'pink', label: 'Rose', bg: 'pink.500' },
  { value: 'red', label: 'Rouge', bg: 'red.500' },
  { value: 'orange', label: 'Orange', bg: 'orange.500' },
  { value: 'yellow', label: 'Jaune', bg: 'yellow.500' },
  { value: 'green', label: 'Vert', bg: 'green.500' },
  { value: 'teal', label: 'Turquoise', bg: 'teal.500' },
  { value: 'cyan', label: 'Cyan', bg: 'cyan.500' },
]

export const CreateProjectModal = memo(function CreateProjectModal({
  isOpen,
  onClose,
  onSubmit
}: CreateProjectModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    specialty: 'general',
    type: 'research',
    color: 'indigo',
  })

  const handleSubmit = async () => {
    if (!formData.title.trim()) return

    setIsSubmitting(true)
    try {
      await onSubmit(formData)
      setFormData({
        title: '',
        description: '',
        specialty: 'general',
        type: 'research',
        color: 'indigo',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Créer un nouveau projet</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Titre du projet</FormLabel>
              <Input
                placeholder="Ex: Toxoplasmose congénitale - Revue 2025"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="Décrivez les objectifs de ce projet de recherche..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Spécialité</FormLabel>
              <Select
                value={formData.specialty}
                onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
              >
                <option value="general">Général</option>
                <option value="parasitology">Parasitologie</option>
                <option value="hematology">Hématologie</option>
                <option value="radiology">Radiologie</option>
                <option value="immunology">Immunologie</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Type de projet</FormLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <option value="research">Recherche</option>
                <option value="writing">Rédaction</option>
                <option value="clinical_review">Revue clinique</option>
                <option value="case_study">Étude de cas</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Couleur</FormLabel>
              <HStack spacing={2} flexWrap="wrap">
                {COLORS.map((color) => (
                  <Box
                    key={color.value}
                    w="40px"
                    h="40px"
                    bg={color.bg}
                    borderRadius="md"
                    cursor="pointer"
                    border="3px solid"
                    borderColor={formData.color === color.value ? 'gray.800' : 'transparent'}
                    transition="all 0.2s"
                    _hover={{ transform: 'scale(1.1)' }}
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    title={color.label}
                  />
                ))}
              </HStack>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSubmitting}>
            Annuler
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!formData.title.trim()}
          >
            Créer le projet
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
})
